import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { SecuritySetupService } from '@app/modules/security-setup/services/security-setup.service';
import { finalize, forkJoin, from, Observable } from 'rxjs';
import { FormArray, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { ImportsService } from '../../services/imports.service';
import { SpreadsheetComponent, SpreadsheetMainMenuItem } from '@progress/kendo-angular-spreadsheet';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as fileSaver from 'file-saver';
import { Import } from '../../models/imports.model';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TableField } from '@app/modules/security-setup/models/table-field.model';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { Sheet } from '@progress/kendo-spreadsheet-common';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { ColumnMappingDialogComponent } from '../column-mapping-dialog/column-mapping-dialog.component';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

@Component({
  selector: 'app-import-editor',
  templateUrl: './import-editor.component.html',
  styleUrls: ['./import-editor.component.scss']
})
export class ImportEditorComponent implements OnInit, AfterViewInit {
  @ViewChild('stepper', { static: false }) stepper: any;
  loadingTables: boolean;
  tableGroups: any[];
  filteredTableGroups: any[];
  loadingFields: boolean;
  fieldList: TableField[];
  filteredFieldList: TableField[];
  importForm: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl(''),
    description: new UntypedFormControl(''),
    culture: new UntypedFormControl(''),
    sheetName: new UntypedFormControl(''),
    startingRow: new UntypedFormControl(1),
    table: new UntypedFormControl(null),
    columnMappings: new UntypedFormArray([]),
    file: new UntypedFormControl(null)
  });
  tables: any[] = [];
  public sheets;
  public items: SpreadsheetMainMenuItem[] = [];
  currentFile: File;
  fileName: string;
  importId: string;
  downloadedImportFile: any;
  importObject: Import;
  private pendingFileLoad: boolean = false;
  cultures: Culture[] = [];
  flatTables = [
    'tbl_Employees',
    'tbl_Compensations',
    'tbl_PayrollDetails',
    'tbl_EmploymentDetails',
    'tbl_EmploymentRecords',
  ];
  allowedTables = [
    'tbl_Employees',
    'tbl_WorkLocations',
    'tbl_Positions',
    'tbl_PositionsEmployees',
    'tbl_StatutoryHolidays',
    'tbl_StatutoryHolidayGroups',
    'tbl_OrganizationalUnits',
    'tbl_IntegrationEmployees'
  ];
  unwantedEmployeeTables = [
    'tbl_DocumentLibrary',
    'tbl_EmployeeTimeOffPolicies',
    'tbl_ProfilePictures',
    'tbl_StockPurchasePlans',
    'tbl_TimeOffDetails',
  ];
  columnMappingTypes: { id: string; name: string; }[];
  hideValidateButton: boolean = true;
  selectedStep: number;
  showStepper: boolean;

  constructor(
    private securitySetupService: SecuritySetupService,
    private fb: UntypedFormBuilder,
    private overlayService: OverlayService,
    private importsService: ImportsService,
    private router: Router,
    private dialog: MatDialog,
    public translate: TranslateService,
    private importService: ImportsService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private cultureService: CultureService,
  ) {
    this.importId = this.getImportId();
    this.selectedStep = this.getStep() || 0;
    this.cultureService.getCultures().subscribe(cultures => {
      this.cultures = cultures;
    });
  }

  ngOnInit(): void {
    this.getColumnMappingTypes();
    this.getTables();
    if (this.importId) {
      this.hideValidateButton = false;
      this.getImport();
    } else {
      // Only add subscription for new imports, not when editing
      this.setupTableChangeSubscription();
    }

    setTimeout(() => {
      this.showStepper = true;
    }, 500);
  }

  private setupTableChangeSubscription(): void {
    // Add subscription to clear column mappings when table changes
    this.importForm.get('table')?.valueChanges.subscribe(() => {
      this.clearColumnMappings();
      this.addDefaultColumnMappings();
    });
    
    // Clear mapping type when tableField value changes in each column mapping
    const columnMappingsArray = this.importForm.get('columnMappings') as UntypedFormArray;
    columnMappingsArray.valueChanges.subscribe(() => {
      columnMappingsArray.controls.forEach((group: UntypedFormGroup) => {
        group.get('tableField')?.valueChanges.subscribe(() => {
          group.get('type')?.setValue(null);
        });
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.selectedStep > 0) {
      setTimeout(() => {
        for (let i = 0; i < this.selectedStep; i++) {
          this.stepper.steps.toArray()[i].completed = true;
        }
      }, 0);
    }
  }

  get isTableFlat() {
    return this.flatTables.includes(this.importForm.get('table')?.value);
  }

  get showImportButtons() {
    return this.importObject && this.importObject.status && ['PENDING', 'VALIDATINGCOMPLETE', 'VALIDATIONFAILED'].includes(this.importObject.status.id);
  }

  get showSaveButton() {
    if (this.importId) {
      return this.importObject && this.importObject.status && ['PENDING', 'VALIDATINGCOMPLETE', 'VALIDATIONFAILED'].includes(this.importObject.status.id) && this.importForm.dirty;
    }
    return false;
  }

  get columnMappings(): FormArray {
    return this.importForm.get('columnMappings') as UntypedFormArray;
  }

  getImportId(): string {
    let importId: string;

    this.route.paramMap.subscribe(
      params => importId = params.get("importId")
    )

    return importId;
  }

  getStep(): number {
    let step: number;

    this.route.paramMap.subscribe(
      params => step = +params.get("step")
    )

    return step;
  }

  getImport() {
    this.importService.getImport(this.importId)
      .subscribe(
        res => {
          this.importObject = res;

          if (this.importObject.file !== null) {
            this.loadImportFile(this.importId);
          }

          this.importForm.patchValue({
            name: res.name,
            description: res.description,
            culture: res.culture?.id,
            sheetName: res.sheetName,
            startingRow: res.startingRow,
            table: res.table.id
          });

          res.columnMappings.forEach(mapping => {
            const columnMapping = this.fb.group({
              sheetColumn: new UntypedFormControl(mapping.sheetColumn),
              tableField: new UntypedFormControl(mapping.tableField?.id),
              createLookupIfMissing: new UntypedFormControl(mapping.createLookupIfMissing),
              type: new UntypedFormControl(mapping.type?.id)
            });

            (this.importForm.get('columnMappings') as UntypedFormArray).push(columnMapping);
          });

          // Now that we've loaded the import data, set up the subscription for future changes
          this.setupTableChangeSubscription();
        }
      );
  }

  backToImports() {
    this.router.navigate(['/Imports']);
    // Logic to navigate back to the imports page
  }

  addColumnMapping() {
    const columnMapping = this.fb.group({
      sheetColumn: new UntypedFormControl('', Validators.required),
      tableField: new UntypedFormControl(null),
      createLookupIfMissing: new UntypedFormControl(false),
      type: new UntypedFormControl(null),
    });

    (this.importForm.get('columnMappings') as UntypedFormArray).push(columnMapping);
  }

  removeColumnMapping(index: number) {
    (this.importForm.get('columnMappings') as UntypedFormArray).removeAt(index);
  }

  openColumnMappingDialog(columnMapping?: any) {
    let newColumnMapping = false;

    if (columnMapping === undefined) {
      this.addColumnMapping();
      newColumnMapping = true;
      columnMapping = columnMapping || this.columnMappings.at(this.columnMappings.length - 1);
    }

    const dialogRef = this.dialog.open(ColumnMappingDialogComponent, {
      data: {
        columnMapping: columnMapping,
        fields: this.getSelectedTableFields(),
        columnMappingTypes: this.columnMappingTypes
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === false && newColumnMapping === true) {
        this.removeColumnMapping(this.columnMappings.length - 1);
      }
    });
  }

  getColumnMappingsControls() {
    return (this.importForm.get('columnMappings') as UntypedFormArray).controls;
  }

  get itemsFormArray(): FormArray {
    return this.importForm.get('columnMappings') as FormArray;
  }

  getTables() {
    this.loadingTables = true;

    from(this.securitySetupService.getAllTables())
      .pipe(
        finalize(() => {
          this.loadingTables = false;
          this.getTableFields();
        }
        )
      )
      .subscribe(
        res => {
          this.tables = res.data;
          this.tables = this.tables.filter(table =>
            this.allowedTables.includes(table.id) ||
            (table.parentTable && table.parentTable.id === 'tbl_Employees' && !this.unwantedEmployeeTables.includes(table.id))
          );
        }
      );
  }

  getTableFields() {
    this.loadingFields = true;

    let observables: Observable<any>[] = [];

    this.tables.forEach(table => {
      observables.push(from(this.securitySetupService.getAllFields(table.id)));
    });

    forkJoin(observables)
      .pipe(
        finalize(() => {
          this.loadingFields = false;
          this.removeUnwantedFields();
        })
      )
      .subscribe(
        (res) => {
          res.forEach((fields, index) => {
            this.tables[index].fields = fields.data;
          }
          );
        }
      );
  }

  removeUnwantedFields() {
    const unwantedFields = [
      {
        parentTableId: 'tbl_Positions',
        fieldId: 'tfi_CostCentre'
      }
    ];

    this.tables.forEach(table => {
      unwantedFields.forEach(unwantedField => {
        if (table.id === unwantedField.parentTableId) {
          table.fields = table.fields.filter((field: TableField) => field.id !== unwantedField.fieldId);
        }
      });
    });
  }

  getSelectedTableFields() {
    const selectedTableId = this.importForm.get('table')?.value;
    const selectedTable = this.tables.find(table => table.id === selectedTableId);
    return selectedTable ? selectedTable.fields : [];
  }

  getTableFieldName(fieldId: string): string {
    const selectedTableFields = this.getSelectedTableFields();
    const field = selectedTableFields?.find(f => f.id === fieldId);
    return field ? field.name : '';
  }

  getColumnMappingTypeName(typeId: string): string {
    const type = this.columnMappingTypes.find(t => t.id === typeId);
    return type ? type.name : '';
  }

  isAlookupField(fieldId: string): boolean {
    const selectedTableFields = this.getSelectedTableFields();
    const selectedField = selectedTableFields?.find(field => field.id === fieldId);

    if (!selectedField || !selectedField.properties) {
      return false;
    }
    return selectedField.properties.some(
      (prop: { property: string; value: string }) =>
        prop.property === 'ListGroup' && !!prop.value
    );
  }

  getColumnMappingTypes() {
    this.importsService.getColumnMappingTypes()
      .subscribe(
        res => {
          this.columnMappingTypes = res.data;
        }
      );
  }

  canSave(): boolean {
    const columnMappings = this.importForm.get('columnMappings') as UntypedFormArray;
    return columnMappings.length > 0 && this.importForm.valid;
  }

  save() {
    if (!this.canSave()) {
      this.snackbarService.openSnackBar(
        this.translate.instant('PleaseAddAtLeastOneColumnMapping', { Default: 'Please add at least one column mapping' }),
        'clear',
        'error'
      );
      return;
    }

    this.overlayService.show();

    if (this.importObject) {
      this.importsService.updateImport(this.importObject.id, this.importForm.value)
        .subscribe(
          {
            next: (v) => {
              this.upload(this.importObject.id);
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }
    else {
      this.importsService.createImport(this.importForm.value)
        .subscribe(
          {
            next: (v) => {
              this.upload(v.id);
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }
  }

  selectFile(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file: File = event.target.files[0];
      this.hideValidateButton = true;
      this.currentFile = file;
      this.fileName = this.currentFile.name;
    } else {
      this.fileName = 'Select File';
    }
  }

  stepChange(event: StepperSelectionEvent) {
    this.selectedStep = event.selectedIndex;
  }

  upload(importId: string) {
    if (this.currentFile) {
      this.importsService.uploadImportFile(importId, this.currentFile, this.currentFile.name).subscribe(
        (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            // this.progress = Math.round(100 * event.loaded / event.total);
          } else if (event instanceof HttpResponse) {
            this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
            if (this.router.url === `/Imports/Editor/${importId}`) {
              // Refresh the current route
              this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate([`/Imports/Editor/${importId}/2`]);
              });
            } else {
              this.router.navigate([`/Imports/Editor/${importId}/2`]);
            }
          }
        },
        (err: any) => {
          this.overlayService.hide();

          this.currentFile = undefined;
        });
    }
  }

  downloadImportFile() {
    fileSaver.saveAs(this.downloadedImportFile);
  }

  loadImportFile(importId: string) {
    this.overlayService.show(this.translate.instant('Loading'));

    this.importsService.downloadImportFile(importId)
      .subscribe(
        {
          next: (v) => {
            let blob: any = new Blob([v.body], { type: v.body.type });
            this.downloadedImportFile = blob;
            this.currentFile = new File([blob], 'imported-file.xlsx', { type: blob.type });
            this.fileName = this.currentFile.name;
            this.overlayService.hide();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  openConfirmImportDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      localizationCode: 'ImportDataWarningMessage'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.triggerImport();
        }
      }
    );
  }

  openConfirmDeleteMappingDialog(index: number) {
    const mapping = this.columnMappings.at(index).value;
    const isDefaultMapping = (mapping.type === 'USER_SYSTEM_ID' || mapping.type === 'PARENT_SYSTEM_ID') && !mapping.tableField;
    if (isDefaultMapping) {
      const dialogConfig = new MatDialogConfig();

      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;

      dialogConfig.data = {
        localizationCode: 'DeleteImportMappingWarningMessage'
      };

      const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(
        data => {
          if (data === true) {
            this.removeColumnMapping(index);
          }
        }
      );
    }
    else {
      this.removeColumnMapping(index);
    }
  }

  triggerImport() {
    if (this.importObject) {
      this.overlayService.show(this.translate.instant('TriggeringImport'));
      this.importsService.triggerImport(this.importObject.id)
        .pipe(
          finalize(() => {
            this.overlayService.hide();
          })
        )
        .subscribe(
          res => {
            this.snackbarService.openSnackBar(`${this.translate.instant('ImportTriggered')}`, 'clear', 'success');
            this.importObject.status = { id: 'QUEUED', name: 'Queued' };
          }
        );
    }
  }

  validateImportFile() {
    if (this.importObject) {
      this.overlayService.show(this.translate.instant('ValidatingImportFile'));

      this.importsService.validateImportFile(this.importObject.id)
        .pipe(
          finalize(() => {
            this.overlayService.hide();
          })
        )
        .subscribe(
          res => {
            this.snackbarService.openSnackBar(`${this.translate.instant('ImportFileValidated')}`, 'clear', 'success');
          }
        );
    }
  }

  private clearColumnMappings(): void {
    const columnMappings = this.importForm.get('columnMappings') as UntypedFormArray;
    while (columnMappings.length !== 0) {
      columnMappings.removeAt(0);
    }
  }

  addDefaultColumnMappings(): void {
    if (this.columnMappings.length !== 0 || !this.importForm.get('table')?.value) {
      return;
    }

    const selectedTableId = this.importForm.get('table')?.value;
    const selectedTable = this.tables.find(table => table.id === selectedTableId);
    const firstColumnMapping = this.fb.group({
      sheetColumn: new UntypedFormControl('A'),
      tableField: new UntypedFormControl(null),
      createLookupIfMissing: new UntypedFormControl(false),
      type: new UntypedFormControl('USER_SYSTEM_ID')
    });
    const secondColumnMapping = this.fb.group({
      sheetColumn: new UntypedFormControl('B'),
      tableField: new UntypedFormControl(null),
      createLookupIfMissing: new UntypedFormControl(false),
      type: new UntypedFormControl('PARENT_SYSTEM_ID')
    });

    if (selectedTable && selectedTable.parentTable && selectedTable.parentTable.id === 'tbl_Employees') {
      (this.importForm.get('columnMappings') as UntypedFormArray).push(firstColumnMapping);
      (this.importForm.get('columnMappings') as UntypedFormArray).push(secondColumnMapping);
    }
    else if (selectedTable && this.allowedTables.includes(selectedTable.id)) {
      (this.importForm.get('columnMappings') as UntypedFormArray).push(firstColumnMapping);
    }
  }
}